/**
 * All campaign copy. Edit here, not in components.
 *
 * SOURCING RULE FOR THIS FILE: every factual claim about DoorDash, the federal
 * task force, or H.R. 9720 carries a `sources` entry pointing at something a
 * hostile reader can check. A campaign page's only asset is being right. If you
 * add a claim you cannot link, it does not go on the page.
 *
 * `deletionSteps` no longer carry a `sources` field — provenance now lives as a
 * comment above each step. The DashPass step is still transcribed from
 * DoorDash's official help center, and the article it came from is linked in
 * that comment. The "Delete the account" step is not: the site owner walked
 * the deletion flow on a live DoorDash account on 2026-07-23 and is the source
 * of record for that step's button names and its delete-account URL
 * (https://www.doordash.com/consumer/privacy/delete_account), by explicit
 * instruction. Do NOT "fix" that step's text back against the help center, and
 * do not delete it as unsourced — the owner's live walkthrough IS the source.
 */

export interface Source {
  label: string;
  url: string;
}

// Hero opening copy, supplied verbatim by the campaign owner (advocacy voice,
// not a demand-card claim). No em-dashes. The site writes the bill "HR 9720"
// (owner decision, 2026-10-17); only source-link titles keep "H.R. 9720".
//
// Paragraph 2 was REPLACED on 2026-09-21 by explicit owner instruction: the
// H.R. 9720 paragraph that used to sit here (the "vocal support ... 20-cent fee
// for food access programs for low-income families" copy) came out, and the
// owner's sentence about the Austin shooting went in verbatim, with commas
// around the name per the review decision. Do NOT restore the old paragraph:
// H.R. 9720 still has its own demand card, which carries the sourced version of
// that argument. The name is spelled "Garcés Perez" to match the NBC News
// article body and the owner's own text; Garcés Perez was SHOT and survived,
// so nothing here or anywhere on the page may say he was killed.
//
// 2026-10-17: the win paragraph went in FIRST, and the closing call now names
// the two demands still open. Both are drafts written for the owner's review.
// The win is sourced to DoorDash's own release of October 1, 2026, "Today,
// DoorDash withdrew its letter of support for H.R. 9720" (fetched and read),
// and is carried with that link on the H.R. 9720 demand card.
export const heroLede: string[] = [
  'DoorDash finally withdrew support for HR 9720 on October 1st! This bill would have given Congress the ability to block any tax increase that DC passes. Thank you to everyone who deleted the app and told DoorDash why.',
  'Unfortunately, the fight is not over. DoorDash has remained silent as their drivers are killed by ICE and other federal police. Now ICE has shot Wilber Rafael Garcés Perez, who delivers food for DoorDash.',
  'DoorDash also has not called for an end to the DC Safe and Beautiful Task Force. DC communities are calling on all allies to keep #DeleteDoorDash going until those two demands are met.',
];

/** A run of step text; an object segment renders as a link. */
export type StepSegment = string | { text: string; url: string };

export interface DeletionStep {
  title: string;
  body: StepSegment[];
}

export interface Demand {
  id: string;
  /** Short imperative used in the demands list and the hero. */
  headline: string;
  /**
   * The line shown on the card face, beneath the headline. Optional: a demand
   * whose headline already IS the whole ask (the task force) omits it.
   */
  ask?: string;
  body: string[];
  /** Optional pull-quote rendered inside the demand, after `quoteAfter`. */
  quote?: {
    text: string;
    attribution: string;
    /** Substring of `text` to emphasize. Must appear in `text` verbatim. */
    emphasize: string;
  };
  /** Index in `body` after which `quote` renders. Ignored when `quote` is unset. */
  quoteAfter?: number;
  /**
   * Set once DoorDash has met the demand. The card keeps its headline (the
   * demand as it was made) and shows a "Won" badge with this ISO date.
   */
  wonOn?: string;
  sources: Source[];
}

// Each demand is defined individually below, then assembled into `demands` in
// the campaign's chosen display order at the end of this block. Reordering the
// display list must not touch any demand's body, quote, ask, or sources.
const hr9720Demand: Demand = {
    id: 'hr9720',
    // DoorDash's support for the bill is ON THE RECORD as of July 22, 2026:
    // NOTUS reported that Comer said during the committee debate that the
    // food-delivery platform supported his bill, "which DoorDash confirmed to
    // NOTUS." That article is the first source below and it is the only thing
    // holding up this demand's central claim. Until it published, this page
    // deliberately argued the point instead of asserting it, because nothing
    // established it. The rule has not changed: the claim travels with its
    // source. If that link ever dies, this demand comes back down to argument.
    headline: 'Immediately withdraw your support for HR 9720.',
    // WON, October 1, 2026. DoorDash's own release (first source below,
    // fetched and read 2026-10-17): "Today, DoorDash withdrew its letter of
    // support for H.R. 9720". The headline stays as the demand was made; the
    // card face now reports the result. The Rettig sentence is verbatim in
    // both that release and the Washington Sun article.
    wonOn: '2026-10-01',
    ask: 'Done. On October 1, 2026, DoorDash withdrew its letter of support for HR 9720. Public pressure works.',
    body: [
      'HR 9720, the D.C. Taxing Authority Review Act, was introduced by House Oversight Chairman James Comer. It would require an affirmative vote of Congress before any DC law imposing or increasing a tax or fee could take effect. Today those measures become law after a passive review period. Under this bill, if Congress simply does nothing for 60 days, the District\u2019s legislation dies. Delegate Norton, Councilmember Robert White, and the ACLU of DC all call it what it is: a rollback of home rule. Free DC, which is organizing opposition to it, calls it “a direct attack on DC’s ability to self-govern.” Mayor Bowser opposes it too, on the plainest possible ground: “the federal government should not touch the District’s ability to raise and spend its local dollars.”',
      'Comer said during the committee debate that the food-delivery platform supported his bill. John Horton, the company’s head of North American policy, said the decision was motivated by the DC Council’s recent approval of a 20-cent fee on all third-party deliveries in the city. A standing congressional veto over the District’s tax code in answer to a fee of twenty cents is ridiculous.',
      'When the Council added the 20-cent fee to delivery orders, DoorDash answered with a petition drive, social ads, a commissioned poll, and a mobile billboard outside the Wilson Building. It lost anyway. DC’s elected government voted, and the fee passed. HR 9720 is the appeal from that loss, taken not to the voters of the District but to a Congress in which the District has no vote, and it would apply to every tax the District ever passes.',
      'Here is how the company explains itself:',
      'A company that has \u201calways supported home rule\u201d does not hand Congress a veto over the laws of a city with no vote in Congress. Whatever you think of the delivery fee, the remedy DoorDash signed on to is not a hearing, or a smaller fee, or a better bill. It is a permanent lever over the District\u2019s budget, held by a Congress in which no District resident has a vote, still there long after this fee is forgotten. Home rule you abandon the moment it costs you money is not support for home rule.',
      // 2026-10-17: the old closing paragraph ("So the ask is small, and it is
      // free. Withdraw the support...") was replaced by the result. Every fact
      // here is in the DoorDash release or the Washington Sun article, both
      // fetched and read: the withdrawal and its date, Rettig's title and his
      // sentence verbatim, Robert White's "did not happen in a vacuum", and
      // the Sun's report that the bill "has since stalled".
      'So we asked DoorDash to withdraw its support. On October 1, 2026, it did. Max Rettig, the company\u2019s head of global public policy, said: \u201cWe recognize that working directly with District leaders is the right way to fight for District residents.\u201d Councilmember Robert White said the reversal \u201cdid not happen in a vacuum.\u201d It did not. Residents deleted the app, advocates and elected officials spoke up, and DoorDash backed down. The bill has since stalled in the House.',
    ],
    // Pulled out of `body` so it renders as a blockquote rather than a paragraph.
    // VERBATIM from the NOTUS article linked first in `sources` \u2014 the statement
    // Horton wrote. An earlier version of this file carried a longer composite
    // whose opening sentence ("...diapers, prescriptions... without holding a
    // single public hearing") could not be traced to any published source. It
    // was cut deliberately. DO NOT restore it, do not extend this quote by a
    // word that is not printed in the linked article, and match the article's
    // capitalization: NOTUS prints "home rule" lowercase, so this does too.
    quote: {
      text: 'We\u2019ve said from the start that taxes like this fall hardest on the people D.C. says it wants to protect, and nothing has changed. We have always supported home rule for D.C., but will continue to do everything we can to defend D.C.\u2019s small businesses, local economy, and underserved residents.',
      attribution: 'John Horton, DoorDash’s head of North American policy, in a statement to NOTUS',
      /** Rendered emphasized in the blockquote \u2014 it is the whole point of citing it. */
      emphasize: 'We have always supported home rule for D.C.',
    },
    /** Paragraph index in `body` after which the quote is rendered. */
    quoteAfter: 3,
    // The NOTUS piece comes first: it carries the demand's central claim, that
    // DoorDash supported the bill and confirmed it. Free DC second, for the
    // home-rule movement's position on the bill. Free DC's page predates the
    // DoorDash story and does not mention the company \u2014 do not cite it for
    // anything about DoorDash.
    // The two October 1 sources lead: they carry the result. NOTUS follows,
    // for the original claim that DoorDash supported the bill.
    sources: [
      {
        label: 'DoorDash: DoorDash Withdraws Support for H.R. 9720, Reaffirms Commitment to D.C. (Oct. 1, 2026)',
        url: 'https://about.doordash.com/en-us/news/doordash-commitment-to-dc',
      },
      {
        label: 'The Washington Sun: DoorDash Apologizes for Siding With GOP Bill Targeting D.C. (Oct. 1, 2026)',
        url: 'https://www.washingtonsun.com/metro/doordash-apology-gop-bill-dc',
      },
      {
        label:
          'NOTUS: House Committee Advances Bill Limiting D.C.\u2019s Ability to Raise Taxes (July 22, 2026)',
        url: 'https://www.notus.org/metro/house-bill-dc-taxes',
      },
      {
        label: 'Free DC: Oppose H.R. 9720, the \u201cD.C. Taxing Authority Review Act\u201d',
        url: 'https://freedcproject.org/news/oppose-hr-9720-the-dc-taxing-authority-review-act',
      },
      // Bowser opposes the bill but does NOT use "home rule" or "rollback"
      // language, so body[0] quotes what she actually said rather than folding
      // her into the rollback list — an earlier revision named her in that list
      // with nothing backing it, which was an overclaim. This statement was
      // fetched and read in full (2026-07-23): mayor.dc.gov returns 200, the
      // release is dated July 22, 2026, and the quoted sentence is verbatim.
      //
      // The League of Women Voters was dropped from that list entirely. LWVUS
      // has a page on congressional interference in DC affairs, but it returns
      // 403 to every client available here, so it could not be read. An unread
      // source does not go on this page — that is the whole rule.
      {
        label:
          'Mayor Bowser: Statement opposing the D.C. Taxing Authority Review Act (July 22, 2026)',
        url: 'https://mayor.dc.gov/release/statement-mayor-bowser-opposing-dc-taxing-authority-review-act',
      },
      {
        label: 'Congress.gov: H.R. 9720, D.C. Taxing Authority Review Act',
        url: 'https://www.congress.gov/bill/119th-congress/house-bill/9720/text',
      },
      {
        label: 'Rep. Norton: Remarks on the markup to strip DC\u2019s authority over its tax code',
        url: 'https://norton.house.gov/media/press-releases/norton-releases-remarks-oversight-markup-bill-strip-dcs-authority-over-its-own',
      },
      {
        label: 'Councilmember Robert White: Comer bill would give Congress control over D.C.\u2019s money',
        url: 'https://www.robertwhiteatlarge.com/councilmember-robert-white-comer-bill-would-give-congress-control-over-d-c-s-money/',
      },
      {
        label: 'ACLU of D.C.: House vote attacks local democracy',
        url: 'https://www.acludc.org/press-releases/house-vote-attacks-dc-democracy/',
      },
      {
        label:
          'DoorDash: Higher Costs, Lower Earnings: How D.C.\u2019s New Delivery Tax is Failing the District',
        url: 'https://about.doordash.com/en-us/news/doordash-dc-delivery-tax',
      },
    ],
};

const iceDemand: Demand = {
    id: 'ice',
    // The headline is the whole ask, so there is no separate `ask` line.
    headline:
      'Issue a public statement against ICE and other federal police who are hunting and killing delivery drivers.',
    body: [
      'On August 11, 2025, federal immigration agents working alongside DC police began targeting app delivery drivers on District streets. Reporting by The Intercept documented warrantless arrests and violent takedowns, and a visible collapse in the number of drivers working the city. Advocates described the tactic plainly: drivers were targeted because they work outdoors, which makes them easy to grab.',
      'DoorDash, Uber, and Grubhub were each asked to comment. None of them responded.',
      'Since then the pattern has cost lives. Joan Sebastián Durán Guerrero, a DoorDash driver, was killed by a federal agent in Maine. Alfredo Alejandro Aljorna, also a DoorDash driver, was killed after being pursued by an ICE agent in Minneapolis on January 14, 2026. Neither was the target of an investigation. Both were working.',
      // Austin, September 20, 2026. Every clause of this paragraph maps to a
      // sentence in the NBC News article that is now the first source below,
      // fetched and read on 2026-09-21: the officer "shot and seriously
      // wounded" him while he "was delivering food in Austin, Texas"; he was
      // "taken to a local hospital and was ‘in serious but stable condition’";
      // his lawyer learned he was "being held in immigration custody at the
      // South Texas ICE Processing Center in Pearsall, Texas"; and a DoorDash
      // spokesperson "confirmed Garcés Perez was making a delivery at the time,
      // adding that DoorDash is investigating and would not release more
      // information ‘at this time.’" He SURVIVED. This paragraph says "shot",
      // never "killed", and no detail may be added to it that is not in that
      // article. If the story develops, that is a new task with a new fetch.
      'On September 20, 2026, an ICE officer shot Wilber Rafael Garcés Perez, a DoorDash driver, while he was delivering food in Austin, Texas. He was hospitalized in serious but stable condition, then taken into ICE custody. DoorDash confirmed he was on a delivery at the time and said it is investigating. It has said nothing else.',
      'These are the people who make the company work. In Trump’s first term, Uber and Lyft publicly opposed the Muslim ban. Now they can’t even bother to speak out against their workers being killed.',
    ],
    sources: [
      // Freshest claim first: this is the article behind the Austin paragraph
      // above, and it is the only source for any Austin fact on this page.
      {
        label:
          'NBC News: ICE officer shoots Venezuelan man delivering food in Austin, lawyer says (Sept. 20, 2026)',
        url: 'https://www.nbcnews.com/news/us-news/ice-shooting-austin-texas-rcna598870',
      },
      {
        label: 'The Intercept: ICE Targets App Delivery Drivers, and the Tech Giants They Work for Stay Silent',
        url: 'https://theintercept.com/2025/08/26/trump-ice-immigrant-delivery-drivers-dc/',
      },
      {
        label: 'CNN: How immigration agents killed 2 US citizens',
        url: 'https://www.cnn.com/2026/07/22/politics/jake-tapper-minnesota-immigration-shootings',
      },
      {
        label: 'Philadelphia Inquirer: Four people killed by ICE agents',
        url: 'https://www.inquirer.com/opinion/editorials/trump-immigration-ice-killings-salgado-guerrero-abuse-power-20260717.html',
      },
    ],
};

const taskForceDemand: Demand = {
    id: 'task-force',
    headline: 'State publicly that the “DC Safe and Beautiful Task Force” should end.',
    // No ask line by campaign direction — the headline is the whole demand. The
    // detail still sits behind the toggle.
    body: [
      'The task force was created by Executive Order 14252 in March 2025 and expanded into a multi-agency federal deployment after the August 11, 2025 executive order declaring a crime emergency in the District. It now spans more than twenty federal and local agencies operating on DC streets.',
      'August 11, 2025 is the same day the sweeps against delivery drivers began. The deployment that the administration describes as making the city safe and beautiful is, for the people delivering its food, the thing that made going to work dangerous.',
      'A company whose entire operation depends on people moving freely through the District does not get to be neutral about an occupation of the District.',
    ],
    sources: [
      {
        label: 'DOJ: D.C. Safe & Beautiful Task Force',
        url: 'https://www.justice.gov/usao-dc/dc-safe-beautiful-task-force-0',
      },
      {
        label: 'U.S. Marshals Service: Task Force reaches milestones',
        url: 'https://www.usmarshals.gov/news/stories/dc-safe-and-beautiful-task-force-reaches-milestones',
      },
    ],
};

// Display order is a deliberate editorial decision. Updated 2026-09-21,
// superseding the 2026-07-26 order: after an ICE officer shot a DoorDash
// driver in Austin, the P1 decision is to lead with the ICE ask. ICE is now
// #1, H.R. 9720 follows it, and the DC task force still closes. Reordering
// this list must not touch any demand's body, ask, quote, or sources, and it
// did not. content.test.ts guards the order.
//
// 2026-10-17: H.R. 9720 is WON and moves to the end, so the two open demands
// read first: ICE, then the task force, then the win.
export const demands: Demand[] = [iceDemand, taskForceDemand, hr9720Demand];

// Provenance lives per-step now, as a comment above each entry — see the file
// header for why. Where a step is still transcribed from DoorDash's help
// center, the rule from before still applies to it: never name a control the
// help center does not name.
export const deletionSteps: DeletionStep[] = [
  // DashPass step: transcribed from the DoorDash help center article —
  // https://help.doordash.com/en-us/consumers/article/how-do-i-cancel-my-dashpass-subscription
  {
    title: 'Cancel DashPass first, if you have it',
    body: [
      'In the DoorDash app or on doordash.com, tap the account icon, go to Manage DashPass, and tap Cancel Membership. Select a reason, tap Select reason, confirm with Cancel DashPass, then tap Got it.',
    ],
  },
  // Delete step: Walked on a live account by the site owner, 2026-07-23. Owner
  // is the source of record; these button names are NOT in the help center and
  // must not be 'corrected' against it.
  {
    title: 'Delete the account',
    body: [
      'To delete via the web, click ',
      { text: 'this link', url: 'https://www.doordash.com/consumer/privacy/delete_account' },
      ' to go to the Delete Account page on DoorDash’s site. This will ask you for two-factor authentication (usually a text). Once that’s complete, you can click the Delete Account button on the page. In the app, Click Profile Icon > Settings > Scroll Down to Manage Account > Scroll Down to Manage Account on this page > Login again > Click Delete Account.',
    ],
  },
];

// ---------- Tell DoorDash why you deleted ----------

export interface DeletionFeedback {
  /** Button label. */
  cta: string;
  /** All recipients, comma-joined into the mailto `to`. */
  to: string[];
  subject: string;
  body: string;
}

// The three addresses are verified from the Elliott Report consumer contacts
// directory for DoorDash (firstname.lastname@doordash.com format). Do NOT add
// any unlisted address (for example, do not invent one for John Horton).
export const deletionFeedback: DeletionFeedback = {
  cta: 'Tell DoorDash why you deleted',
  to: [
    'tony.xu@doordash.com',
    'prabir.adarkar@doordash.com',
    'arielle.salomon@doordash.com',
  ],
  subject: 'Why I deleted my DoorDash account',
  body: [
    'I deleted my DoorDash account today, and I want to tell you why.',
    '',
    'DoorDash has stayed silent while delivery drivers are hunted and killed by ICE and other federal police. DoorDash has not opposed the DC Safe and Beautiful Task Force. You withdrew your support for HR 9720 once DC pushed back. That was one demand out of three.',
    '',
    'Speak out against the federal police targeting delivery drivers, and say the task force should end. Until then I am gone, and I am asking everyone I know to delete DoorDash too.',
  ].join('\n'),
};

// ---------- Spread the Word ----------

export interface SpreadWord {
  heading: string;
  body: string;
  cta: string;
  url: string;
}

export const spreadWord: SpreadWord = {
  heading: 'Spread the Word',
  body: 'Help #DeleteDoorDash reach further. The social toolkit has graphics, sample posts, and everything you need to share the campaign with your networks.',
  cta: 'Open the social toolkit',
  url: 'https://docs.google.com/document/d/1C6bcfy4DBK4SBt0DhWUCtiVq4YQcCRyOGOSXrlBwimU/edit?usp=sharing',
};

// ---------- In the News ----------

export interface NewsItem {
  /** Outlet name, shown as the eyebrow (e.g. "NBC News"). */
  outlet: string;
  /** Headline, verbatim from the article. */
  headline: string;
  /** ISO date (YYYY-MM-DD) the article was published. */
  date: string;
  /** One sentence, in the campaign's voice, on why this story matters here. */
  summary: string;
  url: string;
}

export const newsSection = {
  heading: 'In the News',
  lede: 'Coverage of the campaign, and of what is happening to the people who deliver for DoorDash.',
};

// SOURCING RULE FOR THIS LIST: nothing goes in it that has not been fetched and
// read in full. A headline copied from a search result, a summary written from
// memory, or a URL nobody opened is exactly the failure this page cannot afford.
//
// NBC News item: fetched (HTTP 200) and read on 2026-09-21. The headline is
// verbatim from the article's own <h1>; `date` is its datePublished
// (2026-09-20T21:46Z, printed on the page as Sept. 20, 2026, 5:46 PM EDT). The
// summary carries only what the article states: an ICE officer shot him while
// he was delivering food in Austin, and DoorDash confirmed he was making a
// delivery, said it is investigating, and "would not release more information
// ‘at this time.’" He survived; the summary must never say otherwise. The body
// spelling "Garcés Perez" is used throughout the site (the article's subhead
// prints "Garcés Pérez"; its body text does not).
//
// Added 2026-10-17, each fetched and read in full that day. Headlines are
// verbatim; summaries carry only what the article states. Newest first.
// - Washington Sun (Martin Austermuhle, Oct. 1, 2026 10:31 a.m.): the Sun's
//   own words are that Rettig "conceded" the bill was "the wrong way" to
//   fight the fee; DoorDash's release contains no apology, so "apologized"
//   appears only in the Sun's headline, never in our voice.
// - WUSA9 (Alanea Cremen, Oct. 1, 2026): wusa9.com returns 403, so the item
//   links the WUSA9-branded Yahoo News copy that was actually read. White's
//   "Do not run to Congress..." quote is verbatim from it.
// - WTOP (Kyle Cooper, July 24): Dodds quote verbatim.
// - People's World (Jamal Rich, July 24): lists all three demands.
// - WUSA9 (Katie Lusso, Yahoo copy, July 22 ET): Parker deleted the app.
// - DC News Now (Makea Luzader, Yahoo copy, July 22): dcnewsnow.com 403s, as
//   recorded earlier; the syndicated Yahoo copy is readable and is what is
//   linked. Henderson's "feels a little like betrayal" is verbatim.
// - NOTUS (July 22): already read and cited on the H.R. 9720 card.
export const newsItems: NewsItem[] = [
  {
    outlet: 'The Washington Sun',
    headline: 'DoorDash Apologizes for Siding With GOP Bill Targeting D.C.',
    date: '2026-10-01',
    summary:
      'DoorDash withdrew its support for HR 9720, and its head of global public policy conceded that backing the bill was the wrong way to fight the delivery fee. Councilmember Robert White said the reversal “did not happen in a vacuum.”',
    url: 'https://www.washingtonsun.com/metro/doordash-apology-gop-bill-dc',
  },
  {
    outlet: 'WUSA9',
    headline: 'DoorDash withdraws support for Congressional oversight bill, announces new DC initiatives',
    date: '2026-10-01',
    summary:
      'Councilmember Robert White, who pressed DoorDash to drop the bill, had a warning for other companies: “Do not run to Congress and use our lack of democracy against us.”',
    url: 'https://www.yahoo.com/news/politics/articles/doordash-withdraws-support-congressional-oversight-175229705.html',
  },
  {
    outlet: 'NBC News',
    headline: 'ICE officer shoots Venezuelan man delivering food in Austin, lawyer says',
    date: '2026-09-20',
    summary:
      'An ICE officer shot a DoorDash driver, Wilber Rafael Garcés Perez, while he was making a delivery in Austin. DoorDash confirmed he was working and said only that it is investigating.',
    url: 'https://www.nbcnews.com/news/us-news/ice-shooting-austin-texas-rcna598870',
  },
  {
    outlet: 'WTOP',
    headline: 'DoorDash spurs boycott calls for backing a GOP effort to block DC tax changes',
    date: '2026-07-24',
    summary:
      'Free DC co-founder Alex Dodds called DoorDash’s position on home rule “absolutely indefensible” as the group urged residents to delete the app.',
    url: 'https://wtop.com/dc/2026/07/doordash-spurs-boycott-calls-for-backing-a-gop-effort-to-block-dc-tax-changes/',
  },
  {
    outlet: 'People’s World',
    headline: 'DoorDash vs. DC: Company joins Republicans in attacking Home Rule, sparking boycott',
    date: '2026-07-24',
    summary:
      'Lays out all three of Free DC’s demands: speak out against ICE, call for an end to the task force, and withdraw support for HR 9720.',
    url: 'https://peoplesworld.org/article/doordash-vs-dc-company-joins-republicans-in-attacking-home-rule-sparking-boycott/',
  },
  {
    outlet: 'WUSA9',
    headline: 'Calls to boycott DoorDash are growing in DC. Here’s why.',
    date: '2026-07-22',
    summary:
      'Ward 5 Councilmember Zachary Parker said he deleted the app, and Free DC posted its three demands.',
    url: 'https://www.yahoo.com/news/politics/articles/calls-boycott-doordash-growing-dc-015338610.html',
  },
  {
    outlet: 'DC News Now',
    headline: 'Free DC calls for DoorDash boycott over DC Home Rule',
    date: '2026-07-22',
    summary:
      'Councilmember Christina Henderson said DoorDash’s support for the bill “feels a little like betrayal.”',
    url: 'https://www.yahoo.com/news/politics/articles/free-dc-calls-doordash-boycott-222621095.html',
  },
  {
    outlet: 'NOTUS',
    headline: 'House Committee Advances Bill Limiting D.C.’s Ability to Raise Taxes',
    date: '2026-07-22',
    summary:
      'The story that started it: Comer said DoorDash supported his bill, and DoorDash confirmed it.',
    url: 'https://www.notus.org/metro/house-bill-dc-taxes',
  },
];

// ---------- Footer ----------

export interface FooterLink {
  label: string;
  href: string;
  /** External links open in a new tab with noreferrer noopener. */
  external?: boolean;
}

export interface SiteFooterContent {
  /** Runs of credit text; an object segment renders as an inline external link. */
  credit: StepSegment[];
  links: FooterLink[];
}

export const siteFooter: SiteFooterContent = {
  credit: [
    'Powered by ',
    { text: 'Free DC', url: 'https://freedcproject.org/' },
    '. Development by ',
    { text: 'District Josh', url: 'https://districtjosh.com/' },
    '.',
  ],
  links: [
    { label: 'Privacy Policy', href: 'https://freedcproject.org/privacy', external: true },
    { label: 'Contact', href: 'mailto:team@freedcproject.org' },
  ],
};
